import { useEffect, useRef } from 'react'
import { useAtom } from 'jotai'
import { Bug, Minus, X } from 'lucide-react'
import { Switch } from '@duck/ui'
import { useKonamiCode } from '@/hooks/useKonamiCode'
import { useAuth } from '@/hooks/useAuth'
import {
  debugDevEnabledAtom,
  debugPanelStateAtom,
  germanUiEnabledAtom,
} from '@/store/debugAtoms'
import i18n from '@/modules/i18n/i18n'
import { applyDocumentDirection } from '@/modules/i18n'
import { getEffectiveUiLanguage } from '@/modules/i18n/uiLanguageVisibility'
import { AccessGroupsTester } from './AccessGroupsTester'

// Funky dashed accent shared by the open panel and the minimized bubble.
const FUNKY_BORDER = 'border-2 border-dashed border-fuchsia-500/70'

/**
 * Hidden internal debug panel, summoned via the Konami code (↑↑↓↓←→←→ b a).
 * A fixed floating widget pinned to the top-right of every page. Copy is
 * intentionally hardcoded English — not translated. Desktop-only.
 */
export function DebugPanel() {
  const [state, setState] = useAtom(debugPanelStateAtom)
  const [germanEnabled, setGermanEnabled] = useAtom(germanUiEnabledAtom)
  const [debugDevEnabled, setDebugDevEnabled] = useAtom(debugDevEnabledAtom)
  const { isAdmin } = useAuth()

  // Konami toggles between open and closed; from minimized it re-opens.
  useKonamiCode(() => setState((prev) => (prev === 'open' ? 'closed' : 'open')))

  // Reconcile the active language whenever the German-UI flag FLIPS at runtime,
  // non-destructively: enabling restores a stored 'de'; disabling reverts active
  // to 'en'. The stored preference is never written here.
  //
  // Skip the initial mount run: on mount the active language is already correct
  // (i18n init + LanguageSync), so reconciling here would clobber a URL-derived
  // language — e.g. on a prerendered /he page, localStorage is empty so
  // getEffectiveUiLanguage() is 'en', which would force <html lang="en"> and
  // break the prerender's lang assertion. Only actual flag toggles should act.
  const didMountLangReconcile = useRef(false)
  useEffect(() => {
    if (!didMountLangReconcile.current) {
      didMountLangReconcile.current = true
      return
    }
    const target = getEffectiveUiLanguage()
    if (i18n.language !== target) {
      void i18n.changeLanguage(target).then(() => applyDocumentDirection(target))
    }
  }, [germanEnabled])

  if (state === 'closed') return null

  if (state === 'minimized') {
    return (
      <button
        type="button"
        dir="ltr"
        aria-label="Open debug panel"
        onClick={() => setState('open')}
        className={`fixed right-4 top-4 z-[60] flex items-center gap-1.5 rounded-full ${FUNKY_BORDER} bg-background/95 px-3 py-1.5 font-mono text-xs font-bold text-fuchsia-600 shadow-lg backdrop-blur transition-transform hover:-translate-y-0.5 hover:rotate-3 dark:text-fuchsia-400`}
      >
        <Bug className="size-4" />
      </button>
    )
  }

  return (
    <div
      dir="ltr"
      className={`fixed right-4 top-4 z-[60] w-64 rounded-xl ${FUNKY_BORDER} bg-background/95 font-mono shadow-xl backdrop-blur`}
    >
      <header className="flex items-center justify-between gap-2 rounded-t-[0.625rem] bg-gradient-to-r from-fuchsia-500/20 to-cyan-500/20 px-3 py-2">
        <span className="flex items-center gap-1.5 text-sm font-bold tracking-wide text-fuchsia-600 dark:text-fuchsia-400">
          <Bug className="size-4" />
          debug
        </span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            aria-label="Minimize debug panel"
            onClick={() => setState('minimized')}
            className="rounded p-1 text-muted-foreground transition-colors hover:bg-foreground/10 hover:text-foreground"
          >
            <Minus className="size-4" />
          </button>
          <button
            type="button"
            aria-label="Close debug panel"
            onClick={() => setState('closed')}
            className="rounded p-1 text-muted-foreground transition-colors hover:bg-destructive/15 hover:text-destructive"
          >
            <X className="size-4" />
          </button>
        </div>
      </header>

      <div className="divide-y divide-border">
        <div className="flex items-center justify-between px-3 py-3">
          <span className="text-sm font-medium">debugDev</span>
          <Switch checked={debugDevEnabled} onCheckedChange={setDebugDevEnabled} />
        </div>
        <div className="flex items-center justify-between px-3 py-3">
          <span className="text-sm font-medium">German UI</span>
          <Switch checked={germanEnabled} onCheckedChange={setGermanEnabled} />
        </div>
        {isAdmin && <AccessGroupsTester />}
      </div>
    </div>
  )
}
